
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Clock, User, Calendar, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import Loading from '../../components/Loading';

// Import the necessary contexts
import { useNotification } from '../../context/NotificationContext';
import { useSocket } from '../../context/SocketContext';
import { useLoggedInUser } from '../../context/LoggedUser';

const apiUrl = process.env.REACT_APP_API_URL;

const toInputDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ExtensionRequestCard = ({ task, extension, onReview, formatDate }) => {
  const [editableSoftDeadline, setEditableSoftDeadline] = useState(toInputDate(extension.new_soft_deadline));
  const [editableHardDeadline, setEditableHardDeadline] = useState(toInputDate(extension.new_hard_deadline));
  const [submittingStatus, setSubmittingStatus] = useState(null);

  const handleApprove = async () => {
    setSubmittingStatus('approving');
    try {
      await onReview(
        task,
        extension,
        'Approved',
        'Approved by admin',
        editableSoftDeadline,
        editableHardDeadline
      );
    } catch (error) {
      setSubmittingStatus(null);
    }
  };

  const handleReject = async () => {
    setSubmittingStatus('rejecting');
    try {
      await onReview(
        task,
        extension,
        'Rejected',
        'Rejected by admin',
        extension.new_soft_deadline,
        extension.new_hard_deadline
      );
    } catch (error) {
      setSubmittingStatus(null);
    }
  };

  const isSubmitting = submittingStatus !== null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
      {/* Card Header and Body (No changes here, so keeping it collapsed for brevity) */}
      <div className="bg-gradient-to-r from-[#8d9df8] to-[#4462fa] animate-gradient-x px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white">{task.title}</h3>
            <p className="text-blue-100 text-sm mt-1">Task ID: {task.taskId}</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
              <span className="text-white text-xs font-medium">PENDING REVIEW</span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-6">{/* ... all the card content ... */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Requested by</p>
              <p className="text-sm text-gray-600">{extension.requested_by?.name || 'Unknown'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Calendar className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">New Soft Deadline</p>
              <input
                type="date"
                value={editableSoftDeadline}
                min={toInputDate(task.soft_deadline)}
                onChange={(e) => setEditableSoftDeadline(e.target.value)}
                disabled={isSubmitting}
                className="mt-1 block w-full text-sm text-gray-700 bg-gray-50 border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200"
              />
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Clock className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">New Hard Deadline</p>
              <input
                type="date"
                value={editableHardDeadline}
                min={toInputDate(task.hard_deadline)}
                onChange={(e) => setEditableHardDeadline(e.target.value)}
                disabled={isSubmitting}
                className="mt-1 block w-full text-sm text-gray-700 bg-gray-50 border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200"
              />
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Original Deadlines</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500">Soft Deadline</p>
              <p className="text-sm text-gray-700">{formatDate(task.soft_deadline)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Hard Deadline</p>
              <p className="text-sm text-gray-700">{formatDate(task.hard_deadline)}</p>
            </div>
          </div>
        </div>
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-2">
            <FileText className="h-4 w-4 text-gray-500" />
            <h4 className="text-sm font-medium text-gray-900">Reason for Extension</h4>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 leading-relaxed">{extension.reason}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
          <button
            className="flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200 font-medium disabled:bg-green-400 disabled:cursor-not-allowed"
            onClick={handleApprove}
            disabled={isSubmitting}
          >
            {submittingStatus === 'approving' ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Approving...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Approve Extension
              </>
            )}
          </button>
          <button
            className="flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200 font-medium disabled:bg-red-400 disabled:cursor-not-allowed"
            onClick={handleReject}
            disabled={isSubmitting}
          >
            {submittingStatus === 'rejecting' ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Rejecting...
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 mr-2" />
                Reject Extension
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ExtensionRequests = () => {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize context hooks
  const { createNotification } = useNotification();
  const { socket } = useSocket();
  const { loggedInUser } = useLoggedInUser();

  const fetchExtensions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/api/task/get-extension-requests`, {
        withCredentials: true,
      });
      setExtensions(res.data);
    } catch (err) {
      console.error('Failed to load extension requests:', err);
      toast.error('Failed to fetch extension requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (
    task,
    extension,
    status,
    comment,
    softDeadline,
    hardDeadline,
  ) => {
    const receiverId = extension.requested_by?._id;
    if (!receiverId) {
      console.error("Cannot send notification: Receiver ID is missing.");
    }
    
    try {
      const payload = {
        status,
        comment,
        new_soft_deadline: softDeadline ? new Date(softDeadline).toISOString() : undefined,
        new_hard_deadline: hardDeadline ? new Date(hardDeadline).toISOString() : undefined,
      };

      await axios.patch(
        `${apiUrl}/api/task/${task._id}/review-extension/${extension._id}`,
        payload,
        { withCredentials: true }
      );

      toast.success(`Extension ${status.toLowerCase()} successfully`);

      if (receiverId && loggedInUser) {
        const notificationMessage = `Your extension request for task '${task.title}' has been ${status.toLowerCase()} by ${loggedInUser.name}.`;

        socket.emit("extension-review", {
          to: receiverId,
          message: notificationMessage,
          date: new Date(),
        });

        await createNotification({
          sender: loggedInUser._id,
          receiver: [receiverId],
          message: notificationMessage,
          notDesc: task.taskId,
        });
      }

      fetchExtensions();
    } catch (err) {
      console.error('Review failed:', err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || 'Failed to review extension request');
      throw err;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'
    });
  };

  useEffect(() => {
    fetchExtensions();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Extension Requests</h1>
              <p className="mt-2 text-sm text-gray-600">Review and manage deadline extension requests from team members</p>
            </div>
            <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {extensions.length} pending request{extensions.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {extensions.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No extension requests</h3>
            <p className="text-gray-600">All caught up! No pending extension requests to review.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {extensions.map(({ task, extension }) => (
              <ExtensionRequestCard
                key={extension._id}
                task={task}
                extension={extension}
                onReview={handleReview}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtensionRequests;